import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { ManagerCallParams, ManagerCallResult, OrganisationNodeRestParams, OrganisationNode, DeleteResponse, OrganisationNodeParams } from '@sysarb/sysarb-types/services/v1/organisation-node';
import type { UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';
import { toValue, type MaybeRefOrGetter } from 'vue';

const service = 'base-registry-service';

const restService = new RestManager(`https://${getApiHost()}/${service}`);

export type DeleteNodeResult = { id: string, reason: 'used' | 'call' };

export type SetOrganisationManagerPayload = { organisationNodeId: string, managerId: string };
export type RemoveOrganisationManagerPayload = { organisationNodeId: string, managerId: string };
export type SetOrganisationDeputyManagerPayload = { organisationNodeId: string, managerId: string };
export type RemoveOrganisationDeputyManagerPayload = { organisationNodeId: string, managerId: string };
export type UpdateOrganisationNodePayload = { organisationNodeId: string, params: OrganisationNodeParams };
export type CreateOrganisationNodePayload = OrganisationNode;
export type SetActiveOrganisationNodePayload = { id: string, isActive: boolean };

type OrganisationNodeOptions = {
    queries: {
        getOrganisationNodes: (params: MaybeRefOrGetter<OrganisationNodeRestParams>, enabled?: boolean) => ReturnType<typeof queryOptions<OrganisationNode[] | null, Error>>,
        getPossibleManagers: (params: MaybeRefOrGetter<ManagerCallParams>, enabled?: boolean) => ReturnType<typeof queryOptions<ManagerCallResult[] | null, Error>>,
    },
    mutations: {
        delete: UseMutationOptions<DeleteNodeResult | null, Error, string>,
        setOrganisationManager: UseMutationOptions<void | null, Error, SetOrganisationManagerPayload>,
        removeOrganisationManager: UseMutationOptions<void | null, Error, RemoveOrganisationManagerPayload>,
        setOrganisationDeputyManager: UseMutationOptions<void | null, Error, SetOrganisationDeputyManagerPayload>,
        removeOrganisationDeputyManager: UseMutationOptions<void | null, Error, RemoveOrganisationDeputyManagerPayload>,
        update: UseMutationOptions<void | null, Error, UpdateOrganisationNodePayload>,
        create: UseMutationOptions<OrganisationNode | null, Error, OrganisationNode>,
        setActive: UseMutationOptions<void | null, Error, SetActiveOrganisationNodePayload>,
    },
};

export const organisationNodes: OrganisationNodeOptions = {
    queries: {
        getOrganisationNodes: (params: MaybeRefOrGetter<OrganisationNodeRestParams>, enabled: boolean = true): ReturnType<typeof queryOptions<OrganisationNode[] | null, Error>> => {
            const { instance } = useLoginStore();
            const url = `/v1/${instance}/organisation-tree`;
            return queryOptions<OrganisationNode[] | null, Error>({
                queryKey: [service, url, toValue(params)],
                queryFn: async (): Promise<OrganisationNode[] | null> => {
                    try {
                        return await restService.post<OrganisationNode[]>(toValue(params), url);
                    } catch (error) {
                        console.error('getOrganisationNodes', error);
                        return null;
                    }
                },
                enabled: enabled
            });
        },
        getPossibleManagers: (params: MaybeRefOrGetter<ManagerCallParams>, enabled: boolean = true): ReturnType<typeof queryOptions<ManagerCallResult[] | null, Error>> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.organisation-nodes`;
            const method = 'possible-managers';
            return queryOptions<ManagerCallResult[] | null, Error>({
                queryKey: [service, topic, method, toValue(params)],
                queryFn: async (): Promise<ManagerCallResult[] | null> => {
                    const resgate = ResgateManager.getInstance();
                    return resgate.call<ManagerCallResult[] | null>(topic, method, toValue(params)).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
                enabled: enabled
            });
        }
    },
    mutations: {
        delete: {
            mutationFn: async (id: string): Promise<DeleteNodeResult | null> => {
                const { instanceTopic } = useLoginStore();
                const resgate = ResgateManager.getInstance();
                return resgate.call<DeleteResponse | null>(`v1.${instanceTopic}.organisation-node.${id}`, 'delete-v2', {}).then(res => {
                    if (res instanceof ResErr) {
                        return <DeleteNodeResult> {
                            id: id,
                            reason: 'used'
                        };
                    }

                    if (res == null) {
                        return <DeleteNodeResult>{
                            id: id,
                            reason: 'call'
                        };
                    }

                    return null;
                }).catch(() => {
                    return <DeleteNodeResult>{
                        id: id,
                        reason: 'call'
                    };
                });
            },
        },
        setOrganisationManager: {
            mutationFn: async (payload: SetOrganisationManagerPayload): Promise<void | null> => {
                const { instanceTopic } = useLoginStore();
                const resgate = ResgateManager.getInstance();
                const params: Pick<OrganisationNodeParams, 'manager'> = {
                    manager: payload.managerId
                };
                return resgate.call<void>(`v1.${instanceTopic}.organisation-node.${payload.organisationNodeId}`, 'set', params).then(res => {
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                });
            }
        },
        removeOrganisationManager: {
            mutationFn: async (payload: RemoveOrganisationManagerPayload): Promise<void | null> => {
                const { instanceTopic } = useLoginStore();
                const resgate = ResgateManager.getInstance();
                const params: Pick<OrganisationNodeParams, 'removeManager'> = {
                    removeManager: payload.managerId
                };
                return resgate.call<void>(`v1.${instanceTopic}.organisation-node.${payload.organisationNodeId}`, 'set', params).then(res => {
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                });
            }
        },
        setOrganisationDeputyManager: {
            mutationFn: async (payload: SetOrganisationDeputyManagerPayload): Promise<void | null> => {
                const { instanceTopic } = useLoginStore();
                const resgate = ResgateManager.getInstance();
                const params: Pick<OrganisationNodeParams, 'managerDeputy'> = {
                    managerDeputy: payload.managerId
                };
                return resgate.call<void>(`v1.${instanceTopic}.organisation-node.${payload.organisationNodeId}`, 'set', params).then(res => {
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                });
            }
        },
        removeOrganisationDeputyManager: {
            mutationFn: async (payload: RemoveOrganisationDeputyManagerPayload): Promise<void | null> => {
                const { instanceTopic } = useLoginStore();
                const resgate = ResgateManager.getInstance();
                const params: Pick<OrganisationNodeParams, 'removeManagerDeputy'> = {
                    removeManagerDeputy: payload.managerId
                };
                return resgate.call<void>(`v1.${instanceTopic}.organisation-node.${payload.organisationNodeId}`, 'set', params).then(res => {
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                });
            }
        },
        update: {
            mutationFn: async (payload: UpdateOrganisationNodePayload): Promise<void | null> => {
                const { instanceTopic } = useLoginStore();
                const resgate = ResgateManager.getInstance();
                return resgate.call<void>(`v1.${instanceTopic}.organisation-node.${payload.organisationNodeId}`, 'set', payload.params).then(res => {
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                });
            }
        },
        create: {
            mutationFn: async (params: CreateOrganisationNodePayload): Promise<OrganisationNode | null> => {
                const { instanceTopic } = useLoginStore();
                const resgate = ResgateManager.getInstance();
                return resgate.call<OrganisationNode>(`v1.${instanceTopic}.organisation-nodes`, 'create', params).then(res => {
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                });
            }
        },
        setActive: {
            mutationFn: async (payload: SetActiveOrganisationNodePayload): Promise<void | null> => {
                const params: Pick<OrganisationNodeParams, 'active'> = {
                    active: payload.isActive
                };
                const { instanceTopic } = useLoginStore();
                const resgate = ResgateManager.getInstance();
                return resgate.call<void>(`v1.${instanceTopic}.organisation-node.${payload.id}`, 'set', params).then(res => {
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                });
            }
        }
    }
};
